/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react';

import { Box, Flex, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import { isRequestFailure, PaginationType } from '../../hooks/useFetch';
import { CreateButton } from '../buttons/CreateButton';
import { EditButton } from '../buttons/EditButton';
import { ViewButton } from '../buttons/ViewButton';
import { DataSkeleton, EmptyState, ErrorState } from '../states';
import { DeleteButton } from '../buttons/DeleteButton';
import {
  ConfirmDialog,
  deriveAnyLabel,
  deriveFacetLabel,
  Facet,
  FilterBar,
  FramedCard,
  FramedCardContent,
  getFacetIcon,
  PageAction,
  PaginationFooter,
  usePageActionSlot,
} from '../ui';

interface RowActionsProps {
  show?: boolean;
  edit?: boolean;
  delete?: boolean;
}

interface FormatColumnProps {
  field: string;
  formatField: any;
}

interface ColumnsProps {
  name: string;
  label: string | any[];
}

interface DataGridProps {
  columns: ColumnsProps[];
  rows: any;
  requestApi: any;
  error: any;
  baseUrl: string;
  data: any;
  loading: boolean;
  hasCreate?: boolean;
  entityName?: string;
  entityNamePlural?: string;
  deleteAlertMsg?: any;
  infoButton?: any[];
  columnWithAction?: any[];
  rowActions?: RowActionsProps;
  headerFilters?: any[];
  formatColum?: FormatColumnProps[];
  emptyMessage?: JSX.Element;
  onCreate?: () => void;
  onEdit?: (item: any) => void;
  refreshKey?: number;
  rowExtraActions?: (item: any) => React.ReactNode;
}

export const DataGrid = ({
  columns,
  rows,
  requestApi,
  error,
  baseUrl,
  data,
  loading,
  hasCreate,
  entityName,
  entityNamePlural,
  deleteAlertMsg,
  infoButton,
  columnWithAction,
  rowActions,
  headerFilters,
  formatColum = [],
  emptyMessage,
  onCreate,
  onEdit,
  refreshKey,
  rowExtraActions,
}: DataGridProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { node: pageActionNode } = usePageActionSlot();
  const [pagination, setPagination] = useState<PaginationType>({} as PaginationType);
  const [update, setUpdate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [queryParams, setQueryParams] = useState<any>({
    page: pagination?.current_page ?? 1,
    per_page: 10,
  });

  const hasRowActions =
    rowActions?.show || rowActions?.edit || rowActions?.delete || Boolean(rowExtraActions);
  const activeFilterEntries = Object.entries(queryParams).filter(
    ([key, value]) => !['page', 'per_page'].includes(key) && value !== '' && value != null
  );
  const hasActiveFilters = activeFilterEntries.length > 0;
  const entityDictionary: [string, string, string][] = [
    ['extra-materials', 'material extra', 'materiais extras'],
    ['extraMaterials', 'material extra', 'materiais extras'],
    ['students', 'aluno', 'alunos'],
    ['teachers', 'professor', 'professores'],
    ['tutors', 'tutor', 'tutores'],
    ['responsible', 'responsável', 'responsáveis'],
    ['secretaries', 'secretaria', 'secretarias'],
    ['notifications', 'notificação', 'notificações'],
    ['assessments', 'avaliação', 'avaliações'],
    ['topics', 'tópico', 'tópicos'],
    ['categories', 'categoria', 'categorias'],
    ['meetings', 'encontro', 'encontros'],
    ['weeks', 'semana', 'semanas'],
    ['modules', 'módulo', 'módulos'],
    ['templates', 'template', 'templates'],
    ['institutions', 'instituição', 'instituições'],
    ['teams', 'turma', 'turmas'],
    ['users', 'usuário', 'usuários'],
  ];
  const matchedEntity = location.pathname
    .split('/')
    .reverse()
    .map((segment) => entityDictionary.find(([path]) => path === segment))
    .find(Boolean);
  const entitySingular = entityName ?? matchedEntity?.[1] ?? 'registro';
  const pathEntity = entityNamePlural ?? matchedEntity?.[2] ?? 'registros';
  const clearFilters = () => {
    headerFilters?.forEach((filter) => filter?.setValue?.(null));
    setQueryParams({ page: 1, per_page: 10 });
  };
  const createItem = () =>
    onCreate ? onCreate() : navigate(`${location.pathname}/create`, { replace: true });
  const retry = () => requestApi('get', { params: queryParams });
  const getEmptyAction = () => {
    if (hasActiveFilters) return { label: 'Limpar filtros', onClick: clearFilters };
    return undefined;
  };
  const renderEmptyState = () => {
    if (emptyMessage) {
      return (
        <Flex px="16px" py="48px" align="center" justify="center">
          {emptyMessage}
        </Flex>
      );
    }

    return (
      <EmptyState
        variant="bare"
        title={
          hasActiveFilters ? 'Nenhum resultado para estes filtros' : 'Nenhum resultado encontrado'
        }
        description={
          hasActiveFilters
            ? `Altere ou remova os filtros para localizar ${pathEntity}.`
            : `Não existem ${pathEntity} disponíveis para exibição.`
        }
        action={getEmptyAction()}
      />
    );
  };

  const handleDeleteItem = async (itemId: string) => {
    const result = await requestApi('delete', null, `${baseUrl.split('?')[0]}/${itemId}`);
    if (!isRequestFailure(result)) setUpdate((old) => !old);
    setDeleteTarget(null);
  };

  const setFilterValue = (name: string, value: any) =>
    setQueryParams((old) => ({ ...old, [name]: value, page: 1 }));

  const searchFilter = headerFilters?.find((filter) => filter?.name === 'search');
  const facets: Facet[] = (headerFilters ?? [])
    .filter((filter) => filter?.name !== 'search')
    .map((filterProps) => {
      const current = queryParams[filterProps.name];
      const options = (filterProps.options ?? []).map((option: any) => ({
        value: String(option.value),
        label: option.label,
      }));

      const label =
        filterProps.label ?? deriveFacetLabel(filterProps.placeholder, filterProps.name);

      return {
        key: filterProps.name,
        label,
        icon: getFacetIcon(filterProps.name),
        anyLabel: filterProps.anyLabel ?? deriveAnyLabel(label),
        options,
        values: current == null || current === '' ? [] : [String(current)],
        apply: (values: string[]) => {
          const [next] = values;
          const option = (filterProps.options ?? []).find(
            (entry: any) => String(entry.value) === next
          );
          filterProps?.setValue?.(option?.value ?? null);
          setFilterValue(filterProps.name, option?.value ?? '');
        },
        searchable: filterProps.isSearchable !== false,
        onSearchChange: filterProps.onInputChange,
        loading: filterProps.isLoading,
      };
    });

  useEffect(() => {
    const timeout = setTimeout(() => {
      requestApi('get', { params: queryParams });
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [queryParams, update, refreshKey]);

  useEffect(() => {
    setPagination(data?.pagination);
  }, [data]);

  const isInitialLoading = (loading || (!data && !error)) && !data?.data?.length;
  const hasRows = data?.data?.length > 0;

  const createButton = hasCreate ? (
    <CreateButton onClick={createItem}>{`Criar ${entitySingular}`}</CreateButton>
  ) : undefined;

  const filtersBar = (headerFilters || hasCreate) && (
    <FilterBar
      facets={facets}
      hasActiveFilters={hasActiveFilters}
      onClear={clearFilters}
      totalCount={pagination?.total_items}
      countLabel={entitySingular}
      countLabelPlural={pathEntity}
      search={
        searchFilter && {
          value: queryParams.search ?? '',
          onChange: (value: string) => setFilterValue('search', value),
          label: `Buscar ${pathEntity}`,
          placeholder: searchFilter.placeholder ?? 'Buscar',
        }
      }
      action={pageActionNode ? undefined : createButton}
    />
  );

  if (isInitialLoading) {
    return (
      <Box>
        {createButton && <PageAction>{createButton}</PageAction>}
        {filtersBar}
        <DataSkeleton variant="table" rows={6} columns={columns.length + (hasRowActions ? 1 : 0)} />
      </Box>
    );
  }

  if (error && !hasRows) {
    return (
      <Box>
        {createButton && <PageAction>{createButton}</PageAction>}
        {filtersBar}
        <ErrorState
          description={error?.msg || error?.message || 'Verifique a conexão e tente novamente.'}
          onRetry={retry}
        />
      </Box>
    );
  }

  return (
    <Box>
      {createButton && <PageAction>{createButton}</PageAction>}
      {filtersBar}

      {error && hasRows && (
        <Box mb="16px">
          <ErrorState
            title="Não foi possível atualizar os dados"
            description="Os dados anteriores continuam disponíveis. Tente novamente."
            onRetry={retry}
          />
        </Box>
      )}

      <FramedCard>
        <FramedCardContent>
          {hasRows ? (
            <Box
              overflowX="auto"
              opacity={loading ? 0.55 : 1}
              transition="opacity 0.15s ease"
              aria-busy={loading}
            >
              <Table variant="serra">
                <Thead>
                  <Tr bg="surface.frame">
                    {columns?.map((item, index) => (
                      <Th key={item.name} pl={index === 0 ? '16px' : undefined}>
                        <Flex align="center" gap="4px">
                          {item.label}
                          {infoButton?.find((button) => button.name === item.name)?.button}
                        </Flex>
                      </Th>
                    ))}
                    {hasRowActions && (
                      <Th pr="16px" textAlign="right">
                        Ações
                      </Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {rows?.map((item) => (
                    <Tr key={item.id}>
                      {columns?.map((dataItem, index) => (
                        <Td key={dataItem.name} maxW="280px" pl={index === 0 ? '16px' : undefined}>
                          {formatColum.length > 0
                            ? formatColum
                                .find((value) => value.field === dataItem.name)
                                ?.formatField(_.get(item, dataItem.name)) ??
                              columnWithAction
                                ?.find((value) => value.name === dataItem.name)
                                ?.columnAction(item) ??
                              _.get(item, dataItem.name)
                            : columnWithAction
                                ?.find((value) => value.name === dataItem.name)
                                ?.columnAction(item) ?? _.get(item, dataItem.name)}
                        </Td>
                      ))}
                      {hasRowActions && (
                        <Td pr="16px">
                          <Flex justify="flex-end" align="center" gap="2px">
                            {rowExtraActions?.(item)}
                            {rowActions?.show && (
                              <ViewButton
                                onClick={() => {
                                  navigate(`${location.pathname}/${item.id}`, { replace: true });
                                }}
                              />
                            )}

                            {rowActions?.edit && (
                              <EditButton
                                onClick={() => {
                                  if (onEdit) {
                                    onEdit(item);
                                    return;
                                  }
                                  navigate(`${location.pathname}/${item.id}/edit`, {
                                    replace: true,
                                  });
                                }}
                              />
                            )}

                            {rowActions?.delete && (
                              <DeleteButton onClick={() => setDeleteTarget(item.id)} />
                            )}
                          </Flex>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            !error && renderEmptyState()
          )}

          {hasRows && (
            <PaginationFooter
              totalItems={pagination?.total_items}
              itemLabel={entitySingular}
              itemLabelPlural={pathEntity}
              currentPage={pagination?.current_page ?? 1}
              totalPages={pagination?.total_pages ?? 1}
              onChange={(page) => setQueryParams((old) => ({ ...old, page }))}
            />
          )}
        </FramedCardContent>
      </FramedCard>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDeleteItem(deleteTarget)}
        title="Confirmar exclusão"
        description={deleteAlertMsg ?? 'Esta ação não pode ser desfeita.'}
        confirmLabel="Excluir"
        isDestructive
      />
    </Box>
  );
};
